import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, resolveImg } from "@/lib/apiClient";
import { useSite } from "@/context/SiteContext";
import Seo from "@/components/Seo";
import PublicLayout from "@/components/PublicLayout";
import VehicleCard from "@/components/VehicleCard";
import WhatsAppButton from "@/components/WhatsAppButton";
import CallButton from "@/components/CallButton";
import { HowItWorks, InsuranceSection, DriverRequirements, CtaBanner } from "@/components/Marketing";
import Reviews from "@/components/Reviews";
import FaqList from "@/components/FaqList";
import { waMessages, fmtPrice } from "@/lib/whatsapp";
import { ArrowRight, Car, ShieldCheck, Zap, MessageCircle, PoundSterling, Wrench, Leaf, Headphones } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1623869675781-80aa31012a5a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const HOME_FAQS = [
  ["How much does taxi rental cost in Manchester?", "Weekly pricing varies by vehicle and whether you choose vehicle-only or insurance-inclusive rental. The current weekly price for each car is shown on its listing."],
  ["Do your cars have Manchester PHV plates?", "Vehicles labelled Manchester PHV Licensed are advertised with that status. Always confirm the current licensing with us before rental."],
  ["Can I rent weekly?", "Yes — all our rentals are weekly and flexible. Any minimum rental period is shown on the vehicle listing."],
  ["Do I need my own insurance?", "For vehicle-only rental you'll normally need suitable Hire & Reward insurance. We may also offer insurance-inclusive rental on some cars, subject to eligibility."],
  ["Can I use the vehicle for Uber or Bolt?", "Our vehicles are suitable for licensed private hire drivers. You remain responsible for meeting each platform's and your council's current requirements — WhatsApp us to check a specific car."],
  ["Is there a deposit?", "A deposit is usually required and varies by vehicle. It's shown on the listing where set and confirmed before rental."],
  ["How quickly can I collect a vehicle?", "If a car is marked Available Now and your documents are ready, collection can usually be arranged quickly. WhatsApp us to confirm."],
];

export default function Home() {
  const { settings, stats } = useSite();
  const [vehicles, setVehicles] = useState([]);

  useEffect(() => {
    api.get("/vehicles", { params: { sort: "recently_added" } }).then((r) => setVehicles(r.data)).catch(() => {});
  }, []);

  const availableVehicles = vehicles.filter((v) => v.availability === "available");
  const flagged = availableVehicles.filter((v) => v.show_on_homepage);
  const homepageVehicles = flagged.length > 0 ? flagged : availableVehicles;
  const from = fmtPrice(stats.cheapest_price);
  const count = stats.available_count;
  const carsWord = count === 1 ? "Car" : "Cars";

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://eazycartrack.co.uk/#organization",
        name: settings?.business_name || "EazyCarTrack PHV Private Hire Rental",
        url: "https://eazycartrack.co.uk",
        email: settings?.email || "info@eazycartrack.co.uk",
        telephone: settings?.phone || "07448743318",
      },
      {
        "@type": "WebSite",
        "@id": "https://eazycartrack.co.uk/#website",
        url: "https://eazycartrack.co.uk",
        name: "EazyCarTrack PHV Private Hire Rental",
        publisher: { "@id": "https://eazycartrack.co.uk/#organization" },
      },
      {
        "@type": "AutoRental",
        "@id": "https://eazycartrack.co.uk/#business",
        name: settings?.business_name || "EazyCarTrack PHV Private Hire Rental",
        description:
          "Manchester-licensed private hire vehicles for flexible weekly rental, with or without insurance.",
        url: "https://eazycartrack.co.uk",
        image: HERO_IMG,
        telephone: settings?.phone || "07448743318",
        email: settings?.email || "info@eazycartrack.co.uk",
        priceRange: "££",
        areaServed: ["Manchester", "Salford", "Bolton", "Rochdale", "Oldham", "Trafford", "Greater Manchester"].map(
          (a) => ({ "@type": "City", name: a })
        ),
        address: { "@type": "PostalAddress", addressLocality: "Manchester", addressRegion: "Greater Manchester", addressCountry: "GB" },
        parentOrganization: { "@id": "https://eazycartrack.co.uk/#organization" },
      },
    ],
  };

  return (
    <PublicLayout>
      <Seo
        title="Manchester PHV & Taxi Car Rental"
        description="EazyCarTrack rents Manchester-licensed private hire vehicles to drivers on flexible weekly terms, with or without insurance. See live availability and enquire fast."
        path="/"
        jsonLd={jsonLd}
        ogImage={HERO_IMG}
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-ink text-white">
        <img
          src={HERO_IMG}
          alt="Toyota Corolla Hybrid — Manchester PHV rental"
          className="absolute inset-0 h-full w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/80 to-transparent" />
        <div className="container-x relative py-20 sm:py-28">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider backdrop-blur">
              <span className="live-dot" /> {count} PHV {carsWord} Available Now
            </span>
            <h1 className="mt-6 font-heading text-5xl font-black uppercase leading-[0.95] tracking-tighter sm:text-6xl lg:text-7xl">
              Taxi Rental <span className="text-[#00E676]">Manchester</span>
              <span className="mt-2 block text-3xl font-bold normal-case tracking-tight text-white/90 sm:text-4xl">PHV Cars Ready to Work</span>
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-zinc-300">
              {settings?.homepage_subtext ||
                "EazyCarTrack rents Manchester-licensed private hire vehicles to drivers on flexible weekly terms. Rent with or without insurance and get on the road."}
            </p>

            {from && (
              <div className="mt-8 flex items-baseline gap-2" data-testid="hero-from-price">
                <span className="text-sm font-bold uppercase tracking-wider text-zinc-400">From</span>
                <span className="font-heading text-5xl font-black text-[#00E676]">{from}</span>
                <span className="text-lg font-semibold text-zinc-300">/ week</span>
              </div>
            )}

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/cars"
                data-testid="hero-view-cars"
                className="inline-flex h-14 items-center justify-center gap-2 rounded-full bg-white px-8 font-bold text-ink transition-transform hover:-translate-y-0.5"
              >
                View Available Cars <ArrowRight className="h-5 w-5" />
              </Link>
              <WhatsAppButton size="lg" message={waMessages.general()} context="hero" testid="hero-whatsapp">
                Check Availability on WhatsApp
              </WhatsAppButton>
              <CallButton size="lg" variant="light" context="hero" testid="hero-call">Call Us</CallButton>
            </div>

            <p className="mt-8 text-sm font-semibold uppercase tracking-wider text-zinc-400">
              Manchester PHV • Weekly Rental • Insurance Options • Hybrid Cars • Fast WhatsApp Enquiries
            </p>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="border-b border-border bg-surface">
        <div className="container-x grid gap-6 py-8 sm:grid-cols-3">
          {[
            { icon: Car, t: "Manchester PHV Fleet", d: "Vehicles suited to private hire work" },
            { icon: ShieldCheck, t: "With or Without Insurance", d: "Flexible options subject to eligibility" },
            { icon: Zap, t: "Fast WhatsApp Enquiries", d: "No forms — message us and arrange it" },
          ].map((f, i) => (
            <div key={i} className="flex items-center gap-4">
              <f.icon className="h-8 w-8 text-[#00E676]" />
              <div>
                <div className="font-heading font-bold">{f.t}</div>
                <div className="text-sm text-zinc-500">{f.d}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Available cars */}
      <section className="container-x py-20" id="cars">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Live Inventory</p>
            <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight sm:text-4xl">
              Manchester PHV Cars Available Now
            </h2>
          </div>
          <Link to="/cars" className="inline-flex items-center gap-1.5 font-bold text-zinc-900 hover:underline" data-testid="home-see-all">
            See all cars <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {homepageVehicles.length === 0 ? (
          <div className="mt-10 border border-dashed border-border p-12 text-center text-zinc-500">
            No vehicles to show right now. WhatsApp us for current availability.
          </div>
        ) : (
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {homepageVehicles.map((v, i) => (
              <VehicleCard key={v.id} v={v} index={i} />
            ))}
          </div>
        )}
      </section>

      {/* Why choose */}
      <section className="container-x py-20">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Why EazyCarTrack</p>
        <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight sm:text-4xl">Built Around Private Hire Drivers</h2>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[
            { icon: Car, t: "Manchester PHV Vehicles", d: "A focused fleet chosen for private hire work — not leisure hire." },
            { icon: PoundSterling, t: "Transparent Weekly Pricing", d: "Clear weekly prices, deposit and any minimum period on every listing." },
            { icon: Leaf, t: "Fuel-Efficient Hybrids", d: "Economical hybrids that help keep your running costs down." },
            { icon: ShieldCheck, t: "Insurance Options", d: "Rent vehicle-only, or ask about insurance-inclusive subject to eligibility." },
            { icon: Wrench, t: "Maintained Vehicles", d: "Cars kept roadworthy so you spend more time earning." },
            { icon: Headphones, t: "Fast WhatsApp Support", d: "Real answers over WhatsApp — no forms, no call centres." },
          ].map((f, i) => (
            <div key={i} className="border border-border p-6 transition-colors hover:border-zinc-900">
              <f.icon className="h-8 w-8 text-[#00E676]" />
              <h3 className="mt-4 font-heading text-lg font-bold">{f.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-zinc-600">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      <HowItWorks />
      <InsuranceSection />
      <DriverRequirements />
      <Reviews />

      {/* SEO content */}
      <section className="border-t border-border bg-surface py-16">
        <div className="container-x max-w-4xl">
          <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">
            Manchester Taxi & Private Hire Car Rental, Made Simple
          </h2>
          <div className="mt-5 space-y-4 leading-relaxed text-zinc-600">
            <p>
              EazyCarTrack provides taxi and PHV rental in Manchester for licensed private hire drivers who want to
              get on the road and start earning quickly. We keep a focused fleet of well-maintained private hire
              vehicles available for flexible weekly rental — with or without insurance — so you can choose the
              option that suits how you work. Whether you drive for Uber, Bolt, Veezu or a local operator, we'll
              help you find a car that fits your badge and budget.
            </p>
            <p>
              We're Manchester based with pick-up and support for drivers across Greater Manchester, including
              Salford, Bolton, Rochdale, Oldham and Trafford. Every vehicle listing shows its PHV licence status,
              current availability and a clear weekly price, so there are no surprises. New to the trade? Our{" "}
              <Link to="/guides" className="font-semibold text-zinc-900 underline">driver guides</Link> explain
              licensing, insurance and costs. When you find a car you like, one quick WhatsApp message is all it
              takes.
            </p>
          </div>
        </div>
      </section>

      <FaqList faqs={HOME_FAQS} heading="Manchester Taxi Rental FAQs" testidPrefix="home-faq" />

      <CtaBanner message={waMessages.general()} />
    </PublicLayout>
  );
}
